import React from "react";
import { ProviderContext, withSnackbar } from "notistack";
import { FormattedMessage, injectIntl, WrappedComponentProps } from "react-intl";
import { Button, TextField } from "@material-ui/core";
import _size from "lodash/size";
import PopupHeader, { PositionTitle } from "../../../popup/PopupHeader/PopupHeader";
import PopupAction from "../../../popup/PopupAction/PopupAction";
import Collection from "../../../../domain/Collection";
import container from "../../../../container";
import FrontApiErrorCodeEnum from "../../../../services/WedApi/Error/FrontApiErrorCodeEnum";
import JSONSchemaValidator, { ValidationErrors } from "../../../../services/JSONSchemaValidator/JSONSchemaValidator";

interface StateInterface {
    loading: boolean;
    fieldCollectionDescription: string;
}

interface PropsInterface extends WrappedComponentProps, ProviderContext {
    close: (isUpdate?: boolean) => void;
    collection: Collection;
}

class ChangeDescriptionCollection extends React.Component<PropsInterface, StateInterface> {
    schema = {
        type: "object",
        properties: {
            fieldCollectionDescription: {
                type: "string",
                maxLength: 500,
            },
        },
        required: [],
        additionalProperties: false,
        errorMessage: {
            properties: {
                fieldCollectionDescription: this.props.intl.formatMessage({ id: "app.createCollection.error.collectionDescription" }),
            },
        },
    };

    readonly state = {
        loading: false,
        fieldCollectionDescription: "",
    };

    constructor(props: PropsInterface) {
        super(props);
        this.closePopup = this.closePopup.bind(this);
    }

    componentDidMount() {
        this.setState({ fieldCollectionDescription: this.props.collection.getDescription() });
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        this.setState({ fieldCollectionDescription: value });
    };

    validate = (): { valid: boolean; formErrors: ValidationErrors } => {
        let valid = true;
        const formErrors = this.validateForm();
        if (_size(formErrors)) {
            valid = false;
        }
        return { valid, formErrors };
    };

    validateForm = (): ValidationErrors => {
        const { errors } = JSONSchemaValidator.validate({ fieldCollectionDescription: this.state.fieldCollectionDescription }, this.schema);

        return errors;
    };

    handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (this.state.loading) {
            return;
        }

        this.setState({ loading: true });

        const { valid, formErrors } = this.validate();
        if (!valid) {
            const firstKey: string = Object.keys(formErrors)[0];
            this.props.enqueueSnackbar(formErrors[firstKey], { variant: "error" });
            this.setState({ loading: false });
            return;
        }

        try {
            await container.collectionActionApiService.changeDescriptionCollection(
                this.props.collection.getId(),
                this.state.fieldCollectionDescription
            );
            this.props.enqueueSnackbar(this.props.intl.formatMessage({ id: "app.changeDescriptionCollection.success.rename" }), {
                variant: "success",
            });
            this.props.close(true);
        } catch (e) {
            let keyTranslate = "app.error.undefined";
            if (e.code === FrontApiErrorCodeEnum.ERR_COLLECTION_ID_INVALID) {
                keyTranslate = "app.error.errCollectionIDInvalid";
            } else if (e.code === FrontApiErrorCodeEnum.ERR_COLLECTION_DESCRIPTION_INVALID) {
                keyTranslate = "app.error.errCollectionDescriptionInvalid";
            }
            this.props.enqueueSnackbar(this.props.intl.formatMessage({ id: keyTranslate }), { variant: "error" });
            this.setState({ loading: false });
        }
    };

    private closePopup() {
        this.props.close();
    }

    render() {
        return (
            <div className="delete-collection">
                <PopupHeader positionHeader={PositionTitle.CENTER}>
                    <FormattedMessage id="app.changeDescriptionCollection.header.title" />
                </PopupHeader>
                <form onSubmit={this.handleSubmit} noValidate>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="renameCollection"
                        label={<FormattedMessage id={"app.createCollection.form.collectionDescription"} />}
                        name="changeDescription"
                        autoComplete="off"
                        value={this.state.fieldCollectionDescription}
                        onChange={this.handleChange}
                        multiline
                        rows={5}
                    />
                    <PopupAction>
                        <Button type="submit" className="button--more-padding" disabled={this.state.loading}>
                            <FormattedMessage id="app.standard.popup.button.save" />
                        </Button>
                        <Button onClick={this.closePopup} className="button--more-padding">
                            <FormattedMessage id="app.standard.popup.button.cancel" />
                        </Button>
                    </PopupAction>
                </form>
            </div>
        );
    }
}

export default withSnackbar(injectIntl(ChangeDescriptionCollection));
